'use client';
import { useTranslation } from '@/app/i18n/client';
import useLocation from '@/context/LocationProvider';
import { getNameLocations } from '@/utils/locations';
import { LocationType } from '@/utils/types';
import { Loader2, MapPin } from 'lucide-react';
import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AlertDialog, AlertDialogContent } from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

interface FormData {
  location: LocationType | '';
}

const ModalSetLocation = () => {
  const {
    handleSetLocation,
    isOpenModalSetLocation,
    setIsOpenModalSetLocation,
    locations,
    handleFetchAllLocations,
    locationModal,
  } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const { lng } = useParams() as { lng: string };
  const { t } = useTranslation(lng, ['locations']);

  const {
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<FormData>({
    defaultValues: {
      location: '',
    },
  });

  const handleSubmitSetLocation = async ({ location }: FormData) => {
    setIsLoading(true);
    await handleSetLocation(location as LocationType);
    setIsLoading(false);
  };

  useEffect(() => {
    if (isOpenModalSetLocation) handleFetchAllLocations();
  }, [isOpenModalSetLocation]);

  useEffect(() => {
    if (locationModal) {
      setValue('location', locationModal);
    }
  }, [locationModal]);

  return (
    <AlertDialog onOpenChange={setIsOpenModalSetLocation} open={isOpenModalSetLocation}>
      <AlertDialogContent className='w-full max-w-sm'>
        <form onSubmit={handleSubmit(handleSubmitSetLocation)}>
          <div className='space-y-4'>
            <div className='flex items-center justify-center gap-2 py-4 text-center text-xl'>
              <MapPin size={24} />
              <h1 className='text-2xl font-medium'>{t('modalTitleSetLocation')}</h1>
            </div>
            <div>
              {locations.length ? (
                <Controller
                  name='location'
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select required onValueChange={(value) => field.onChange(value)} {...field}>
                      <SelectTrigger className='mt-2 h-11 w-full'>
                        <SelectValue placeholder={t('Pontos de check-in')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {locations.map(
                            (location) =>
                              !!location.status && (
                                <SelectItem value={location.name} key={location.id}>
                                  <div className='flex gap-2 capitalize'>{getNameLocations(location.name)}</div>
                                </SelectItem>
                              )
                          )}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  )}
                />
              ) : (
                <span className='flex h-11 w-full items-center justify-center text-center'>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  <p className='text-sm text-gray-500'>{t('Carregando pontos de check-in...')}</p>
                </span>
              )}
            </div>
          </div>

          <div className='mt-4 flex h-12 w-full justify-end'>
            <Button
              isLoading={isLoading}
              type='submit'
              disabled={!isValid || isSubmitting || !locations.length}
              className='w-full'>
              Salvar
            </Button>
          </div>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ModalSetLocation;
